import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Users, FileBarChart } from 'lucide-react';
import RangeSelect from '../../components/dashboard/RangeSelect';
import AnalyticsCard from '../../components/dashboard/AnalyticsCard';
import TrafficChart from '../../components/dashboard/TrafficChart';
import TopPages from '../../components/dashboard/TopPages';
import UptimeIndicator from '../../components/dashboard/UptimeIndicator';
import AuditScoreCard from '../../components/dashboard/AuditScoreCard';
import SiteInfoCard from '../../components/dashboard/SiteInfoCard';
import ZoneMissingState from '../../components/dashboard/ZoneMissingState';
import { useAuth } from '../../context/AuthContext';
import { usePortalScope } from '../../context/PortalScopeContext';
import { api } from '../../lib/api';
import PreviewReadyBanner from '../../components/portal/PreviewReadyBanner';
import SiteStatusBanner from '../../components/portal/SiteStatusBanner';
import AuditSummaryCard from '../../components/portal/AuditSummaryCard';
import OpenRequestsCard from '../../components/portal/OpenRequestsCard';
import InvoicesSummaryCard from '../../components/portal/InvoicesSummaryCard';
import DomainStatusCompact from '../../components/portal/DomainStatusCompact';
import SupportTeamCard from '../../components/portal/SupportTeamCard';

function formatNumber(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

export default function Overview() {
  const { user } = useAuth();
  const { account } = usePortalScope();
  const [range, setRange] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);

  // Single-round-trip overview fetch (Sprint D item 3.6). Sprint C's
  // preview-ready banner is now part of this same response.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/client/overview');
        if (!cancelled) setOverview(res || null);
      } catch {
        // Silent — overview blocks render hidden state.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const previewState = overview?.preview_state || null;

  const load = useCallback(async () => {
    if (!account?.id) return;
    setLoading(true);
    setError('');
    try {
      const [a, u] = await Promise.all([
        api.get(`/api/analytics/${account.id}?range=${range}`),
        api.get(`/api/uptime/${account.id}?range=${range}`),
      ]);
      setAnalytics(a);
      setUptime(u);
    } catch (err) {
      setError(err?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [account?.id, range]);

  useEffect(() => {
    load();
  }, [load]);

  const zoneStatus = analytics?.zoneStatus;
  const showZoneMissing = !loading && zoneStatus === 'missing';
  const showTokenMissing = !loading && zoneStatus === 'token_missing';

  const mostPopularPage = useMemo(() => analytics?.topPages?.[0] || null, [analytics]);
  const firstName = user?.name?.split(' ')[0];

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">
            Welcome{firstName ? <>, <span className="serif-acc accent-text">{firstName}</span></> : ''}.
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {account?.name ? `Here's how ${account.name} is performing.` : "Here's your overview."}
          </p>
        </div>
        <RangeSelect value={range} onChange={setRange} disabled={loading} />
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {error}
        </div>
      )}

      <PreviewReadyBanner state={previewState} />
      <SiteStatusBanner project={overview?.project_status} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <AuditSummaryCard audit={overview?.last_audit} />
        <OpenRequestsCard count={overview?.open_requests_count ?? 0} />
        <InvoicesSummaryCard recent={overview?.recent_invoices} />
        <DomainStatusCompact domain={overview?.domain_status} />
        {/* SupportTeamCard moved down to the analytics row to avoid the
            half-empty cell + give the team contacts a permanent prominent
            home on the page (replaces the prior Top referrers panel,
            which was repeatedly empty for school sites because traffic
            is direct or in-site). */}
      </div>

      {showZoneMissing && (
        <div className="mb-8">
          <ZoneMissingState />
        </div>
      )}

      {showTokenMissing && (
        <div className="mb-8 px-4 py-3 rounded-lg bg-yellow-500/[0.08] border border-yellow-500/25 text-[13px] text-yellow-200/90">
          Analytics API token isn't configured on the server. Ask NexSav to finish setup. The rest of your portal still works.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <AnalyticsCard
          icon={Eye}
          label="Total visits"
          value={formatNumber(analytics?.totals?.requests)}
          hint={`Last ${range} days`}
          loading={loading}
          accent="lime"
        />
        <AnalyticsCard
          icon={Users}
          label="Unique visitors"
          value={formatNumber(analytics?.totals?.uniques)}
          hint={`Last ${range} days`}
          loading={loading}
          accent="purple"
        />
        <AnalyticsCard
          icon={FileBarChart}
          label="Most popular page"
          value={mostPopularPage ? truncatePath(mostPopularPage.path) : '—'}
          hint={mostPopularPage ? `${formatNumber(mostPopularPage.views)} views` : `Last ${range} days`}
          loading={loading}
          accent="lime"
        />
      </div>

      <div className="mb-5">
        <TrafficChart data={analytics?.series || []} loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-5">
        {/* Inner grid drops to 1-col when there's no support team configured —
            SupportTeamCard returns null on empty members, so we'd otherwise
            leave a half-empty row. */}
        <div className={`grid grid-cols-1 gap-5 ${overview?.support_team?.length ? 'md:grid-cols-2' : ''}`}>
          <TopPages
            pages={analytics?.topPages || []}
            siteUrl={account?.siteUrl}
            loading={loading}
            window={analytics?.topPagesWindow}
          />
          <SupportTeamCard members={overview?.support_team || []} />
        </div>
        <div className="flex flex-col gap-5">
          <UptimeIndicator
            status={uptime?.status}
            uptime={uptime?.uptime}
            range={uptime?.range}
            loading={loading}
          />
          <AuditScoreCard
            score={account?.auditScore}
            pdfUrl={
              overview?.last_audit?.id
                ? `/api/client/audits/${overview.last_audit.id}/pdf`
                : null
            }
          />
          <SiteInfoCard account={account} />
        </div>
      </div>
    </div>
  );
}

function truncatePath(path) {
  if (!path) return '—';
  if (path.length > 22) return `${path.slice(0, 20)}…`;
  return path;
}
